import React from "react";
import {
  Navigation,
  Page,
  Storage,
  TaskTabPanel,
  TaskImageClick,
  TaskSelect,
  Figure,
  Image
} from "@bittnerdevelopmentgroup/interactive";

import panel1a from "./assets/PrinEco4_SupplyDemandEquilibrium_p1a.jpg"
import panel1b from "./assets/PrinEco4_SupplyDemandEquilibrium_p1b.jpg"
import panel1c from "./assets/PrinEco4_SupplyDemandEquilibrium_p1c.jpg"
import panel2 from "./assets/PrinEco4_SupplyDemandEquilibrium_p2.jpg"
import panel3 from "./assets/PrinEco4_SupplyDemandEquilibrium_p3.jpg"

class InterestOnReserves extends React.Component {
  constructor(props) {
    super(props)
    this.state = {}
  }

  render() {
    return (
      <div className="supplyDemandEquilibrium">
        <div className="sr-only" aria-hidden="true">
          {[panel1a, panel1b, panel1c, panel2, panel3].map(src => <img alt="" src={src} />)}
        </div>
        <Navigation interactiveId="chapter-3">
          <Page>
            <p>
              The demand curve shows how much consumers would buy at each
              price. The supply curve shows how much producers would sell at each
              price. But how do we know what price consumers and producers
              settle on, and how much of the good actually changes hands?
            </p>
            <p>
              We find the price and quantity where everyone's satisfied: where
              consumers think the good is inexpensive enough to buy all of it, and
              where producers think the price of the good is expensive enough to
              be worth selling.
            </p>
            <p>
              <i>Navigate through the following tabs to learn how these factors are represented graphically. Then, select the Next button to move to the next panel.</i>
            </p>
            <TaskTabPanel
              state={Storage()}
              label="Navigate through the tabs below to learn how these factors are represented graphically."
              contents={[
                {
                  title: "Demand",
                  contents: (
                    <>
                      <p>
                        First, we plot the demand curve for a good. The demand curve for orange juice is shown below. At higher prices, customers demand less orange juice, and at lower prices, they demand more.
                      </p>
                      <div align="center">
                        <Image
                          src={panel1a}
                          alt="A graph of demand curve for orange juice. The X axis is labeled quantity. The Y axis is labeled increase in price. The demand curve is linear with a negative slope."
                          width="80%"
                        />
                      </div>
                    </>
                  ),
                },
                {
                  title: "Supply",
                  contents: (
                    <>
                      <p>
                        Then, we add the supply curve to the same graph. As demonstrated by the upward slope of the supply 
                        curve, suppliers are willing to supply more orange juice at higher prices and less at lower prices.
                      </p>
                      <div align="center">
                        <Image
                          src={panel1b}
                          alt="A graph of supply demand curve for orange juice. The X axis is labeled quantity. The Y axis is labeled increase in price. The demand curve is linear with a negative slope. The supply curve is linear with a positive slope. The two curves intersect."
                          width="80%"
                        />
                      </div>
                    </>
                  ),
                },
                {
                  title: "Equilibrium",
                  contents: (
                    <>
                      <p>
                        The point where those curves intersect is the equilibrium. This is
                        where the quantity of orange juice that consumers demand is equal to
                        the quantity that producers will supply. It is also the price where the
                        market is cleared, meaning that there is no orange juice surplus or
                        shortage.
                      </p>
                      <div align="center">
                        <Image
                          src={panel1c}
                          alt="A graph of supply demand curve for orange juice. The X axis is labeled quantity. The Y axis is labeled increase in price. The demand curve is linear with a negative slope. The supply curve is linear with a positive slope. At the point where the two curves intersect is point E at quantity Q and Price P."
                          width="80%"
                        />
                      </div>
                    </>
                  ),
                },
              ]}
            />
          </Page>
          <Page>
            <p>
              Let's explore the market for a different product: sneakers.
            </p>
            <p>
              By the early 1990s, Adidas moved its production overseas, allowing
              the company to make sneakers more inexpensively. This shifted
              Adidas's supply curve to the right, as shown.
            </p>
            <p>
              <b>Where is the new equilibrium?</b>
            </p>
            <p>
              <i>Select the appropriate point on the graph.</i>
            </p>
            <TaskImageClick
              state={Storage()}
              hotspots={[
                {
                  correct: false,
                  label: "Line S 1 intersects line D 1 at point P 1.",
                  feedback: "Close! This was the equilibrium before the supply curve moved. You should identify the point where D₁ and S₂ intersect.",
                  style: {
                    top: "150px",
                    left: "290px",
                    width: "50px",
                    height: "50px",
                  },
                },
                {
                  correct: false,
                  label: "A point on line S 2 with high price and high quantitiy, where line S 2 intersects no other lines.",
                  feedback: "Not quite! You should identify the point where D₁ and S₂ intersect. This is above that point, which is a situation we'll review in the next panel.",
                  style: {
                    top: "125px",
                    left: "485px",
                    width: "50px",
                    height: "50px",
                  },
                },
                {
                  correct: true,
                  label: "Line S 2 intersecting line D 1 at point P 1",
                  feedback: "This is the point where the new supply curve (S₂) intersects with the demand curve, making it the new equilibrium. Notice that when the supply curve shifted to the right, the quantity went up (Q₂) and the price went down (P₂).",
                  style: {
                    top: "250px",
                    left: "370px",
                    width: "50px",
                    height: "50px",
                  },
                },
              ]}
            >
              <Figure
                alt="A supply and demand curve. The x axis is quantity and the y axis is price. There is one demand curve and two supply curves. Supply 1 shifts to the right to supply 2. There are three possible targets: the point where D1 and S1 intersect, the point where D1 and S2 intersect, and a point on S2."
                src={panel2}
                width="80%"
              />
            </TaskImageClick>
          </Page>
          <Page>
            <p>
              Now that you've learned how to find equilibrium, you might be wondering
              what happens if the price is set at a point other than equilibrium.
              Below is an example of that situation.
            </p>
            <div align="center">
              <Image
                src={panel3}
                alt="Supply and Demand curves for the Salmon Market."
                longAlt={"Supply and Demand curves for the Salmon Market. The x axis quantity is given in pounds per month. The y axis price is per pound. The demand curve, D, is linear with a negative slope. The supply curve, S, is linear with a positive slope. The supply and demand curves intersect at point E, where the equilibrium price is $10 and equilibrium quantity is 500.  Above the equilibrium point are two points C and F. At point C on the demand curve, the price is $15 and the quantity is 250. At point F on the supply curve, the price is $15 and the quantity is 750. As the quantity supplied exceeds the demanded, there is a surplus at a price of $15. Below the equilibrium point are two points A and B. At point A on the supply curve, the price is $5 and the quantity is 250. At point B on the demand curve, the price is $5 and the quantity is 750.  As the quantity demanded exceeds the quantity supplied, there is a shortage at a price of $5. Four arrows pointing inwards to the equilibrium point, E, indicate that the market will balance towards equilibrium if market conditions are set below or above the equilibrium point."}
                width="80%"
              />
            </div>
            <p>
              This example illustrates a hypothetical market for purchasing fresh salmon at a fish market. The graph
              represents the market. The equilibrium price is $10. If the price is set above that, then
              there is a big gap between what consumers will buy (C) and what producers will sell (F) at $15 per
              <nobr> pound. Since producers are trying to sell more than consumers will buy, <span>that creates a </span>
                <TaskSelect
                  state={Storage()}
                  options={["surplus", "shortage",]}
                  label={<span>that creates a </span>}
                  correct={0}
                  feedback={[
                    <span>A surplus occurs whenever the quantity supplied is greater than the quantity demanded.</span>,
                    <span>Remember, a shortage occurs whenever the quantity supplied is less than the quantity demanded.</span>,
                  ]}
                  srOnly
                  inline
                />.</nobr>
            </p>
            <p>
              When the price is set at $5 per pound, this is below the equilibrium price: the amount that consumers
              <nobr> want to buy (B) is way more than the amount that producers want to sell (A). <span>That creates a </span>
                <TaskSelect
                  state={Storage()}
                  options={["surplus", "shortage",]}
                  label={<span>that creates a </span>}
                  correct={1}
                  feedback={[
                    <span>Remember, a surplus occurs whenever the quantity supplied is greater than the quantity demanded.</span>,
                    <span>A shortage occurs whenever the quantity supplied is less than the quantity demanded.</span>,
                  ]}
                  srOnly
                  inline
                />.</nobr>
            </p>
          </Page>
          <Page>
            <p>
              Now that you've learned how to find equilibrium, you might be wondering
              what happens if the price is set at a point other than equilibrium.
              Below is an example of that situation.
            </p>
            <div align="center">
              <Image
                src={panel3}
                alt="Supply and Demand curves for the Salmon Market."
                longAlt={"Supply and Demand curves for the Salmon Market. The x axis quantity is given in pounds per month. The y axis price is per pound. The demand curve, D, is linear with a negative slope. The supply curve, S, is linear with a positive slope. The supply and demand curves intersect at point E, where the equilibrium price is $10 and equilibrium quantity is 500.  Above the equilibrium point are two points C and F. At point C on the demand curve, the price is $15 and the quantity is 250. At point F on the supply curve, the price is $15 and the quantity is 750. As the quantity supplied exceeds the demanded, there is a surplus at a price of $15. Below the equilibrium point are two points A and B. At point A on the supply curve, the price is $5 and the quantity is 250. At point B on the demand curve, the price is $5 and the quantity is 750.  As the quantity demanded exceeds the quantity supplied, there is a shortage at a price of $5. Four arrows pointing inwards to the equilibrium point, E, indicate that the market will balance towards equilibrium if market conditions are set below or above the equilibrium point."}
                width="80%"
              />
            </div>
            <p style={{textWrap: "balance"}}>
              This example illustrates a hypothetical market for purchasing fresh salmon at a fish market. The graph
              represents the market. The equilibrium price is $10. If the price is set above that, then
              there is a big gap between what consumers will buy (C) and what producers will sell (F) at $15 per
              pound. Since producers are trying to sell more than consumers will buy, that creates a
                <TaskSelect
                  state={Storage()}
                  options={["surplus", "shortage",]}
                  label={<span>that creates a </span>}
                  correct={0}
                  feedback={[
                    <span>A surplus occurs whenever the quantity supplied is greater than the quantity demanded.</span>,
                    <span>Remember, a shortage occurs whenever the quantity supplied is less than the quantity demanded.</span>,
                  ]}
                  srOnly
                  inline
                />.
            </p>
            <p>
              When the price is set at $5 per pound, this is below the equilibrium price: the amount that consumers
              want to buy (B) is way more than the amount that producers want to sell (A). That creates a
              <TaskSelect
                state={Storage()}
                options={["surplus", "shortage",]}
                label={<span>that creates a </span>}
                correct={1}
                feedback={[
                  <span>Remember, a surplus occurs whenever the quantity supplied is greater than the quantity demanded.</span>,
                  <span>A shortage occurs whenever the quantity supplied is less than the quantity demanded.</span>,
                ]}
                srOnly
                inline
              />.
            </p>
          </Page>          
        </Navigation>
      </div>
    )
  }
}

export default InterestOnReserves
